import { useEffect, useRef, useState } from "react";
import { Gift, X } from "lucide-react";
import posthog from "posthog-js";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";

const DISMISS_PREFIX = "pendingCancelBannerDismissed:";
/** Show the offer only in the final stretch of the period; earlier it reads
 * as nagging and the user still has the plan they cancelled. */
const SHOW_WITHIN_DAYS = 10;

function dismissKey(periodEnd: string) {
  return `${DISMISS_PREFIX}${periodEnd}`;
}

/** Dismissal is per period end: once the user says "not now" for this cycle
 * we stay quiet until the next time they set a cancel. */
function isDismissed(periodEnd: string) {
  try {
    return Boolean(localStorage.getItem(dismissKey(periodEnd)));
  } catch {
    return false;
  }
}

function planDisplayName(plan: string) {
  const base = plan.replace(/_v2/, "").split("_")[0] ?? plan;
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/**
 * Pending-cancel save offer (web half). A Stripe subscriber who cancelled —
 * in-app or in the billing portal — keeps access until the period ends and
 * nothing used to reach them in that window. This banner offers the stay
 * bonus for one-click reactivation. IAP subscribers get the equivalent
 * prompt in the mobile app (store re-enable + win-back bonus on renewal).
 */
export default function PendingCancelBanner() {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const [dismissedHere, setDismissedHere] = useState(false);
  const [done, setDone] = useState(false);
  const shownTrackedRef = useRef(false);
  const utils = trpc.useUtils();

  const { data: offer } = trpc.payment.getPendingCancelOffer.useQuery(
    undefined,
    { enabled: isAuthenticated && !dismissedHere && !done, staleTime: 5 * 60 * 1000 },
  );
  const keepPlan = trpc.payment.keepPlanFromPendingCancel.useMutation();

  const visible =
    !dismissedHere &&
    !done &&
    !!offer?.eligible &&
    offer.platform === "web" &&
    offer.mode === "reactivate" &&
    !!offer.periodEnd &&
    (offer.daysLeft ?? 99) <= SHOW_WITHIN_DAYS &&
    !isDismissed(offer.periodEnd);

  useEffect(() => {
    if (visible && !shownTrackedRef.current && offer) {
      shownTrackedRef.current = true;
      posthog.capture("pending_cancel_offer_shown", {
        platform: "web",
        plan: offer.plan,
        days_left: offer.daysLeft,
        credits: offer.credits,
      });
    }
  }, [visible, offer]);

  if (!visible || !offer || !offer.periodEnd) return null;

  const periodEnd = offer.periodEnd;
  const plan = offer.plan ? planDisplayName(offer.plan) : "";
  const endDate = new Date(periodEnd).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

  const handleKeep = async () => {
    posthog.capture("pending_cancel_offer_clicked", {
      platform: "web",
      plan: offer.plan,
      credits: offer.credits,
    });
    try {
      const result = await keepPlan.mutateAsync();
      if (result.ok) {
        setDone(true);
        toast.success(
          t("subscription.pendingCancelBanner.kept", {
            credits: result.credits,
          }),
        );
        void utils.payment.getSubscription.invalidate();
        void utils.payment.getPendingCancelOffer.invalidate();
        return;
      }
    } catch {
      // fall through to the generic failure toast
    }
    toast.error(t("subscription.pendingCancelBanner.failed"));
  };

  const handleDismiss = () => {
    posthog.capture("pending_cancel_offer_dismissed", {
      platform: "web",
      plan: offer.plan,
    });
    try {
      localStorage.setItem(dismissKey(periodEnd), String(Date.now()));
    } catch {
      // Storage unavailable — hide for this render only.
    }
    setDismissedHere(true);
  };

  return (
    <div className="fixed bottom-20 left-1/2 z-50 w-[calc(100%-2rem)] max-w-[600px] -translate-x-1/2 lg:bottom-6">
      <div
        className="relative overflow-hidden rounded-[14px]"
        style={{
          background:
            "linear-gradient(150deg, #14121c 0%, #0f0d18 55%, #171224 100%)",
          border: "1px solid rgba(167,139,250,0.28)",
          boxShadow:
            "0 24px 60px -20px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.04)",
        }}
      >
        <div
          className="pointer-events-none absolute"
          style={{
            top: "-60px",
            right: "120px",
            width: "260px",
            height: "180px",
            background:
              "radial-gradient(circle, rgba(167,139,250,0.25), transparent 68%)",
            filter: "blur(20px)",
          }}
        />

        <button
          onClick={handleDismiss}
          aria-label="Dismiss"
          className="absolute right-3 top-3 z-[2] flex h-[30px] w-[30px] items-center justify-center rounded-full text-[#5b6272] transition-colors hover:bg-white/[0.08] hover:text-[#d1d5e0]"
        >
          <X className="h-4 w-4" strokeWidth={2.2} />
        </button>

        <div className="relative flex items-center gap-[14px] py-[14px] pl-4 pr-11">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[rgba(167,139,250,0.14)]">
            <Gift className="h-[18px] w-[18px] text-[#a78bfa]" strokeWidth={2.2} />
          </div>

          <div className="min-w-0 flex-1">
            <h2
              className="m-0 mb-1 text-[15px] font-bold text-white"
              style={{ letterSpacing: "-0.01em" }}
            >
              {t("subscription.pendingCancelBanner.title", { plan, date: endDate })}
            </h2>
            <p
              className="m-0 text-[12px] text-[#9ca3b4]"
              style={{ lineHeight: 1.4 }}
            >
              {t("subscription.pendingCancelBanner.body", {
                credits: offer.credits,
              })}
            </p>
          </div>

          <div className="flex shrink-0 items-center pr-1.5">
            <button
              onClick={handleKeep}
              disabled={keepPlan.isPending}
              className="relative cursor-pointer whitespace-nowrap rounded-full px-5 py-2.5 text-[13px] font-bold text-white transition-transform hover:-translate-y-px active:translate-y-0 disabled:opacity-60"
              style={{
                background: "linear-gradient(90deg, #7c3aed, #a78bfa)",
                boxShadow:
                  "0 12px 30px -8px rgba(124,58,237,0.6), inset 0 1px 0 rgba(255,255,255,0.3)",
                letterSpacing: "-0.01em",
              }}
            >
              {keepPlan.isPending
                ? t("subscription.pendingCancelBanner.ctaBusy")
                : t("subscription.pendingCancelBanner.cta")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
